/**
 * Copyright (c) 2023-present.
 * This source code is copied from https://github.com/justinmahar/react-social-media-embed
 */
import classNames from 'classnames'
import React, { useState, useMemo, useEffect, useRef } from 'react'
import { SkeletonBox, Box } from 'chemistry'
import { useFrame } from '../hooks/useFrame.js'
import { Subs } from 'react-sub-unsub'
import { EmbedStyle } from './embedStyle.js'

const embedJsScriptSrc = 'https://connect.facebook.net/en_US/sdk.js#xfbml=0&version=v17.0'
const borderRadius = 8
const scriptId = 'fb-embed-script'

// Embed Stages
const CHECK_SCRIPT_STAGE = 'check-script'
const LOAD_SCRIPT_STAGE = 'load-script'
const CONFIRM_SCRIPT_LOADED_STAGE = 'confirm-script-loaded'
const PROCESS_EMBED_STAGE = 'process-embed'
const CONFIRM_EMBED_SUCCESS_STAGE = 'confirm-embed-success'
const RETRYING_STAGE = 'retrying'
const EMBED_SUCCESS_STAGE = 'embed-success'

export default function FacebookEmbed({
  url,
  height = 500,
  width = 540,
  hasInstagramEmbed,
  placeholderDisabled = false,
  scriptLoadDisabled = false,
  retryDelay = 5000,
  retryDisabled = false,
  frame = undefined,
  ...divProps
}) {
  const embedId = url.replace(/[?].*$/, '').replace(/^.+\//, '')
  const idRef = useRef(`fb-${embedId}`)
  const [stage, setStage] = useState(CHECK_SCRIPT_STAGE)
  const embedSuccess = useMemo(() => stage === EMBED_SUCCESS_STAGE, [stage])
  const [processTime, setProcessTime] = useState(Date.now())
  const embedContainerKey = useMemo(() => `${idRef.current}-${processTime}`, [processTime])
  const frm = useFrame(frame)

  // === === === === === === === === === === === === === === === === === === ===
  // Embed Stages
  // === === === === === === === === === === === === === === === === === === ===

  // Check Script Stage
  useEffect(() => {
    if (stage === CHECK_SCRIPT_STAGE) {
      if (frm.window?.FB?.XFBML?.parse) {
        setStage(PROCESS_EMBED_STAGE)
      } else if (!scriptLoadDisabled) {
        setStage(LOAD_SCRIPT_STAGE)
      } else {
        console.error('Facebook embed script not found. Unable to process Facebook embed:', url)
      }
    }
  }, [scriptLoadDisabled, stage, url, frm.window])

  // Load Script Stage
  useEffect(() => {
    if (stage === LOAD_SCRIPT_STAGE) {
      const subs = new Subs()
      if (frm.document && !frm.document.getElementById(scriptId)) {
        subs.setInterval(
          () => {
            const scriptElement = frm.document.createElement('script')
            scriptElement.setAttribute('src', embedJsScriptSrc)
            scriptElement.setAttribute('id', scriptId)
            scriptElement.setAttribute('async', true)
            scriptElement.setAttribute('crossorigin', 'anonymous')
            frm.document.head.appendChild(scriptElement)
            setStage(CONFIRM_SCRIPT_LOADED_STAGE)
          },
          hasInstagramEmbed ? 1000 : 1
        )
      }
      if (frm.document.getElementById(scriptId)) {
        setStage(CONFIRM_SCRIPT_LOADED_STAGE)
      }
      return subs.createCleanup()
    }
  }, [stage, frm.document, hasInstagramEmbed])

  // Confirm Script Loaded Stage
  useEffect(() => {
    const subs = new Subs()
    if (stage === CONFIRM_SCRIPT_LOADED_STAGE) {
      subs.setInterval(() => {
        if (frm.window?.FB?.XFBML?.parse) {
          setStage(PROCESS_EMBED_STAGE)
        }
      }, 1)
    }
    return subs.createCleanup()
  }, [stage, frm.window])

  // Process Embed Stage
  useEffect(() => {
    if (stage === PROCESS_EMBED_STAGE) {
      const parse = frm.window?.FB?.XFBML?.parse
      if (parse) {
        parse()
        setStage(CONFIRM_EMBED_SUCCESS_STAGE)
      } else {
        console.error('Facebook embed script not found. Unable to process Facebook embed:', url)
      }
    }
  }, [stage, url, frm.window])

  // Confirm Embed Success Stage
  useEffect(() => {
    const subs = new Subs()
    if (stage === CONFIRM_EMBED_SUCCESS_STAGE) {
      subs.setInterval(() => {
        if (frm.document) {
          const fbPostElem = frm.document.getElementsByClassName('fb-post')?.[0]
          if (fbPostElem) {
            if (fbPostElem.children.length > 0) {
              setStage(EMBED_SUCCESS_STAGE)
            }
          }
        }
      }, 1)
      if (!retryDisabled) {
        subs.setTimeout(() => {
          setStage(RETRYING_STAGE)
        }, retryDelay)
      }
    }
    return subs.createCleanup()
  }, [retryDisabled, retryDelay, stage, frm.document])

  // Retrying Stage
  useEffect(() => {
    if (stage === RETRYING_STAGE) {
      // This forces the embed container to remount
      setProcessTime(Date.now())
      setStage(PROCESS_EMBED_STAGE)
    }
  }, [stage])
  // END Embed Stages
  // === === === === === === === === === === === === === === === === === === ===

  return (
    <Box
      {...divProps}
      className={classNames('rsme-embed rsme-facebook-embed', divProps.className)}
      sx={{
        overflow: 'scroll',
        width: '100%',
        maxWidth: `${width}px`,
        m: '0 auto',
        height: `${height}px`,
        borderRadius,
        ...divProps.style
      }}
    >
      <EmbedStyle />
      <Box id={idRef.current} className={classNames(!embedSuccess && 'rsme-d-none')}>
        <Box
          key={embedContainerKey}
          className='fb-post'
          data-href={url}
          data-width='100%important'
          data-show-text='true'
          sx={{
            width: '100%!important'
          }}
        />
      </Box>
      {!embedSuccess && !placeholderDisabled && <SkeletonBox url={url} height={height} />}
    </Box>
  )
}
